/**
 * Permission catalog — frontend mirror of
 * backend/auth_service/permission_catalog.py.
 *
 * The authoritative copy is served at GET /auth/permissions/catalog; this
 * static mirror lets the permission-builder render instantly (and supplies the
 * lucide icon names) before that request resolves. Keep the two in sync —
 * module ids MUST match the nav item ids in src/shell/NavRail.tsx.
 */

// ── Capability vocabulary ────────────────────────────────────────────────────
export const CAPABILITIES = {
  view: 'Ver',
  create: 'Crear',
  edit: 'Editar',
  delete: 'Eliminar',
  run: 'Ejecutar',
  share: 'Compartir',
  approve: 'Aprobar',
  reject: 'Rechazar',
  sync: 'Sincronizar',
  publish: 'Publicar',
  install: 'Instalar / Desinstalar',
  export: 'Exportar',
} as const;

export type CapabilityKey = keyof typeof CAPABILITIES;

export interface ModuleCapability {
  key: string;
  label: string;
  /** Granting it does not turn its module on (the module row stays off). */
  standalone?: boolean;
}

export interface ResourceType {
  key: string;
  label: string;
  capabilities: CapabilityKey[];
  /** Builder lazy-loads instances from here for "selected" mode. null => all/none only. */
  listEndpoint: string | null;
  service: string | null;
  idField: string;
  labelField: string;
  /** Phase 3: this resource type's instances have fields that can be restricted (e.g. ontology properties). */
  fieldGated?: boolean;
}

export interface CatalogModule {
  module: string;
  label: string;
  icon: string;
  gateable: boolean;
  alwaysVisible?: boolean;
  adminOnly?: boolean;
  superadminOnly?: boolean;
  moduleCapabilities: ModuleCapability[];
  resourceTypes: ResourceType[];
}

// ── Grant grammar (matches the backend) ──────────────────────────────────────
export type ResourceMode = 'all' | 'none' | 'selected';

export interface FieldGrant {
  mode: 'all' | 'none' | 'selected';
  ids: string[];
}

export interface ResourceGrant {
  mode: ResourceMode;
  ids: string[];
  capabilities: string[];
  /** Phase 3: per-instance field restrictions, keyed by resource id. Absent => all fields. */
  field_grants?: Record<string, FieldGrant>;
}

export interface ModuleGrant {
  enabled: boolean;
  moduleCapabilities: string[];
  resources: Record<string, ResourceGrant>;
}

export type Grants = Record<string, ModuleGrant>;

export interface EffectivePermissions {
  role: string;
  /** true => admin/superadmin or legacy "see all" — show everything. */
  full: boolean;
  grants?: Grants;
  modules: string[];
  /** INTRO-13: sube con cada cambio de lo que el usuario ve (propósitos,
   *  perfiles, grupos, marcadores). authFetch lo manda como X-Perm-Version. */
  perm_version?: number;
  /** Active purposes (auth_service/purposes.py). `expires_at` is ISO with an
   *  offset; lib/permSync re-reads the document when the earliest one passes. */
  purposes?: Array<{ activation_id?: string; purpose_id?: string; expires_at?: string }>;
}

const rt = (
  key: string,
  label: string,
  capabilities: CapabilityKey[],
  opts: Partial<Pick<ResourceType, 'listEndpoint' | 'service' | 'idField' | 'labelField' | 'fieldGated'>> = {},
): ResourceType => ({
  key,
  label,
  capabilities,
  listEndpoint: opts.listEndpoint ?? null,
  service: opts.service ?? null,
  idField: opts.idField ?? 'id',
  labelField: opts.labelField ?? 'name',
  fieldGated: opts.fieldGated ?? false,
});

const mc = (key: string, label: string, standalone = false): ModuleCapability =>
  (standalone ? { key, label, standalone } : { key, label });

// ── The catalog — order mirrors the side panel ───────────────────────────────
export const CATALOG: CatalogModule[] = [
  { module: 'apps', label: 'Dashboards', icon: 'LayoutDashboard', gateable: true,
    moduleCapabilities: [mc('create_dashboard', 'Crear dashboards'), mc('author_custom_code', 'Escribir widgets de código (JavaScript)')],
    resourceTypes: [rt('dashboards', 'Dashboards', ['view', 'edit', 'delete', 'share'],
      { listEndpoint: '/apps?kind=dashboard', service: 'ontology', labelField: 'name' })] },
  { module: 'apps-app', label: 'Aplicaciones', icon: 'LayoutDashboard', gateable: true,
    moduleCapabilities: [mc('create_app', 'Crear aplicaciones'), mc('author_custom_code', 'Escribir widgets de código (JavaScript)')],
    resourceTypes: [rt('apps', 'Aplicaciones', ['view', 'edit', 'delete', 'share'],
      { listEndpoint: '/apps?kind=app', service: 'ontology', labelField: 'name' })] },
  // Demo module: nav item only renders for tenant-semex, but it lives in the
  // catalog so its access is manageable like any other module.
  { module: 'traffic', label: 'Centro de Comando', icon: 'TrafficCone', gateable: true,
    moduleCapabilities: [], resourceTypes: [] },
  { module: 'workbench', label: 'Mesa de trabajo', icon: 'BookOpen', gateable: false, alwaysVisible: true,
    moduleCapabilities: [], resourceTypes: [] },
  { module: 'connectors', label: 'Conectores', icon: 'Plug', gateable: true,
    moduleCapabilities: [
      mc('create_connector', 'Crear conectores'),
      mc('manage_egress', 'Administrar la política de salida'),
    ],
    resourceTypes: [rt('connectors', 'Conectores', ['view', 'edit', 'delete', 'sync'],
      { listEndpoint: '/connectors', service: 'connector', labelField: 'name' })] },
  { module: 'ontology', label: 'Ontología', icon: 'Network', gateable: true,
    moduleCapabilities: [mc('edit_schema', 'Editar esquema de ontología')],
    resourceTypes: [rt('object_types', 'Tipos de Objetos', ['view', 'edit', 'delete'],
      { listEndpoint: '/object-types', service: 'ontology', labelField: 'displayName', fieldGated: true })] },
  { module: 'data', label: 'Datos', icon: 'Database', gateable: true,
    moduleCapabilities: [
      mc('data_explorer', 'Usar Explorador de Datos'),
      mc('data_quality', 'Usar Calidad de Datos'),
      mc('view_sensitive', 'Ver valores de campos sensibles (PII alta) sin enmascarar'),
      mc('decrypt', 'Descifrar columnas protegidas en pipelines'),
      mc('process_analysis', 'Analizar todos los procesos del tenant'), mc('manage_processes', 'Administrar procesos del tenant'), mc('manage_indexes', 'Crear índices / promover a hypertable'),
    ],
    resourceTypes: [rt('object_types', 'Tipos de Objetos', ['view'],
      { listEndpoint: '/object-types', service: 'ontology', labelField: 'displayName' })] },
  { module: 'pipelines', label: 'Pipelines', icon: 'Workflow', gateable: true,
    moduleCapabilities: [mc('create_pipeline', 'Crear pipelines'), mc('author_custom_code', 'Escribir pasos de código (Python)')],
    resourceTypes: [rt('pipelines', 'Pipelines', ['view', 'edit', 'delete', 'run'],
      { listEndpoint: '/pipelines', service: 'pipeline', labelField: 'name' })] },
  { module: 'logic', label: 'Logic Studio', icon: 'BrainCircuit', gateable: true,
    moduleCapabilities: [mc('create_function', 'Crear funciones lógicas')],
    resourceTypes: [rt('functions', 'Funciones Lógicas', ['view', 'edit', 'delete', 'run'],
      { listEndpoint: '/logic/functions', service: 'logic', labelField: 'name' })] },
  { module: 'agents', label: 'Agent Studio', icon: 'Bot', gateable: true,
    moduleCapabilities: [
      mc('create_agent', 'Crear agentes'),
      mc('manage_model_providers', 'Administrar proveedores de modelos (BYOM)'),
    ],
    resourceTypes: [rt('agents', 'Agentes', ['view', 'edit', 'delete', 'run'],
      { listEndpoint: '/agents', service: 'agent', labelField: 'name' })] },
  { module: 'external-apps', label: 'App Studio', icon: 'Boxes', gateable: true, adminOnly: true,
    moduleCapabilities: [
      mc('access_studio', 'Abrir Studio / construir aplicaciones'),
      mc('publish_app', 'Publicar aplicaciones en el catálogo del tenant'),
      mc('manage_quotas', 'Administrar cuotas de recursos de aplicaciones'),
    ],
    resourceTypes: [rt('apps', 'Aplicaciones Instaladas', ['view', 'run', 'install'],
      { listEndpoint: '/app-installs', service: 'apps', idField: 'id', labelField: 'app_id' })] },
  { module: 'evals', label: 'Evaluaciones', icon: 'FlaskConical', gateable: true,
    moduleCapabilities: [mc('create_suite', 'Crear suites de evaluación')],
    resourceTypes: [rt('suites', 'Suites de Evaluación', ['view', 'edit', 'run'],
      { listEndpoint: '/suites', service: 'eval', labelField: 'name' })] },
  { module: 'value', label: 'Monitor de Valor', icon: 'TrendingUp', gateable: true,
    moduleCapabilities: [], resourceTypes: [rt('value_streams', 'Flujos de Valor', ['view'])] },
  { module: 'scenarios', label: 'Escenarios', icon: 'BarChart2', gateable: true,
    moduleCapabilities: [mc('create_scenario', 'Crear escenarios')],
    resourceTypes: [rt('scenarios', 'Escenarios', ['view', 'edit', 'run'])] },
  { module: 'activity', label: 'Actividad', icon: 'Activity', gateable: true,
    moduleCapabilities: [mc('export', 'Exportar actividad')], resourceTypes: [] },
  { module: 'insights', label: 'Insights', icon: 'Sparkles', gateable: true,
    moduleCapabilities: [], resourceTypes: [] },
  { module: 'predictions', label: 'Predicciones', icon: 'Target', gateable: true,
    moduleCapabilities: [], resourceTypes: [] },
  { module: 'discovery', label: 'Descubrimiento', icon: 'Compass', gateable: true,
    moduleCapabilities: [mc('create_project', 'Crear proyectos de descubrimiento')],
    resourceTypes: [rt('projects', 'Proyectos de Descubrimiento', ['view', 'edit', 'delete'],
      { listEndpoint: '/discovery/projects', service: 'discovery', labelField: 'name' })] },
  { module: 'task-mining', label: 'Minería de tareas', icon: 'MousePointerClick', gateable: true, adminOnly: true,
    moduleCapabilities: [
      mc('manage_capture', 'Administrar capturas y dispositivos'),
      mc('run_analysis', 'Ejecutar análisis'),
      mc('view_raw_events', 'Ver eventos de captura individuales'),
    ],
    resourceTypes: [] },
  { module: 'operations', label: 'Operaciones', icon: 'Radio', gateable: false, alwaysVisible: true,
    moduleCapabilities: [], resourceTypes: [] },
  { module: 'utilities', label: 'Utilidades', icon: 'Wrench', gateable: true,
    moduleCapabilities: [], resourceTypes: [rt('utilities', 'Utilidades', ['view', 'run'])] },
  { module: 'human-actions', label: 'Acciones', icon: 'ShieldCheck', gateable: true,
    moduleCapabilities: [],
    resourceTypes: [rt('actions', 'Definiciones de Acción', ['view', 'approve', 'reject'],
      { listEndpoint: '/actions', service: 'ontology', idField: 'name', labelField: 'name' })] },
  // Branches and change requests are the release path into main: every
  // builder needs them, and a profile that could hide the review queue would
  // hide the only way a protected tenant accepts a change. Not gateable.
  { module: 'branches', label: 'Ramas', icon: 'GitBranch', gateable: false, alwaysVisible: true,
    moduleCapabilities: [], resourceTypes: [] },
  { module: 'changes', label: 'Cambios', icon: 'GitPullRequest', gateable: false, alwaysVisible: true,
    moduleCapabilities: [], resourceTypes: [] },
  // Solutions (INTRO-3) are on the same release path: the page lists what was
  // published or delivered to the tenant, and every write (package, publish,
  // install, update, roll back) requires a tenant admin on the server.
  { module: 'solutions', label: 'Soluciones', icon: 'Package', gateable: false, alwaysVisible: true,
    moduleCapabilities: [], resourceTypes: [] },
  { module: 'admin', label: 'Administración', icon: 'Shield', gateable: true, adminOnly: true,
    moduleCapabilities: [
      mc('manage_users', 'Administrar usuarios'),
      mc('manage_access', 'Administrar perfiles de acceso / permisos'),
      mc('view_audit', 'Ver registro de auditoría'),
      mc('approve_changes', 'Aprobar y fusionar solicitudes de cambio', true),
    ], resourceTypes: [] },
  // Were gateable:false, so no profile could grant them and the require_superadmin
  // gates in admin-service / incident-service had no capability to migrate onto.
  // Covers the CONSOLE only — operating across tenants is not a capability (a
  // profile is tenant-scoped) and stays with the platform-operator flag.
  // See docs/RBAC_MIGRATION_PLAN.md §2.3.
  { module: 'platform', label: 'Plataforma', icon: 'Globe', gateable: true, superadminOnly: true,
    moduleCapabilities: [
      mc('manage_tenants', 'Crear / editar / eliminar tenants'),
      mc('manage_tiers', 'Editar definiciones de planes'),
      mc('manage_limits', 'Editar cuotas y límites de tasa por tenant'),
      mc('view_platform_usage', 'Ver uso y almacenamiento entre tenants'),
    ], resourceTypes: [] },
  { module: 'incidents', label: 'Incidentes', icon: 'Siren', gateable: true, superadminOnly: true,
    moduleCapabilities: [
      mc('view_incidents', 'Ver cola de incidentes'),
      mc('manage_incidents', 'Triar / resolver incidentes'),
    ], resourceTypes: [] },
  { module: 'settings', label: 'Configuración', icon: 'Settings', gateable: false, alwaysVisible: true,
    moduleCapabilities: [], resourceTypes: [] },
];

export const CATALOG_BY_MODULE: Record<string, CatalogModule> = Object.fromEntries(
  CATALOG.map((m) => [m.module, m]),
);

export const GATEABLE_MODULES: string[] = CATALOG.filter((m) => m.gateable).map((m) => m.module);

// ── Effective-permission read helpers (used by Phase 1 nav/page gating) ──────

/**
 * Is `module` gated by the permission system at all?
 *
 * ONE rule: it is gated if and only if the catalog has a row for it marked
 * `gateable`. Everything else is not gated — and there are two ways to be
 * "everything else", which is what made this subtle:
 *
 *  · A row marked `gateable: false` — workbench, operations, settings. They
 *    carry no capabilities and no resource types on purpose, so `grants.operations`
 *    is never written and no profile can ever enable them.
 *  · No row at all — `academy`, `finance`, `graph`, `insights-runs`, `mirror`,
 *    `projects`. Real, routed pages that were never permission modules.
 *
 * An unknown id used to count as GATEABLE, on the reasoning that a typo'd nav
 * path should not quietly grant access. Sound worry, wrong trade: the frontend
 * gate is an affordance and the backend enforces every endpoint regardless, so
 * failing closed here grants nothing — it just makes a real page silently
 * unreachable, which is exactly how six of them ended up redirecting to
 * Activity. The typo worry is answered instead by
 * `permissionCatalog.pages.test.ts`, which fails the build when a routed page
 * has no catalog row and is not on the explicit non-module list. A test that
 * names the problem beats a default that hides it.
 */
export function isGateableModule(module: string): boolean {
  const row = CATALOG.find((m) => m.module === module);
  return row ? row.gateable !== false : false;
}

/** Module visible / reachable for this user? */
export function moduleEnabled(perms: EffectivePermissions | null, module: string): boolean {
  // Checked FIRST, before `perms` is even consulted: a module the permission
  // system cannot express must not be gated by it. Claiming to deny what you
  // have no way to grant is how Workbench, Operations and Settings disappeared
  // for every user the moment Phase 2 made `full` false for everybody.
  if (!isGateableModule(module)) return true;
  if (!perms) return false;
  if (perms.full) return true;
  return !!perms.grants?.[module]?.enabled;
}

/** Module-level capability granted? */
export function hasModuleCapability(
  perms: EffectivePermissions | null, module: string, capability: string,
): boolean {
  if (!perms) return false;
  if (perms.full) return true;
  return !!perms.grants?.[module]?.moduleCapabilities?.includes(capability);
}

/** Resource-type capability granted for a specific instance (or any, if id omitted)? */
export function canResource(
  perms: EffectivePermissions | null,
  module: string,
  resourceType: string,
  capability: string,
  resourceId?: string,
): boolean {
  if (!perms) return false;
  if (perms.full) return true;
  const r = perms.grants?.[module]?.resources?.[resourceType];
  if (!r) return false;
  if (!r.capabilities.includes(capability)) return false;
  if (r.mode === 'all') return true;
  if (r.mode === 'none') return false;
  return resourceId === undefined ? r.ids.length > 0 : r.ids.includes(resourceId);
}
